import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { adminLogin } from '../lib/api';
import { toast } from 'sonner';
import { Lock, User } from 'lucide-react';

export default function AdminLoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      const result = await adminLogin(username, password);
      toast.success('Login successful');
      onLogin(result.username);
      navigate('/admin');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid username or password';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6]">
      <div className="w-full max-w-md p-8">
        <div className="bg-white border border-[#E2E4E0] p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#1A2E4A] flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl text-[#1A2E4A] mb-2">Admin Login</h1>
            <p className="text-sm text-[#525C6A]">Digital Law Digest</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-[#0A0F1A] mb-2">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#525C6A]" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  className="pl-10 h-12 border-[#E2E4E0] bg-white focus:border-[#1A2E4A] focus:ring-1 focus:ring-[#1A2E4A]"
                  data-testid="admin-username-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[#0A0F1A] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#525C6A]" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="pl-10 h-12 border-[#E2E4E0] bg-white focus:border-[#1A2E4A] focus:ring-1 focus:ring-[#1A2E4A]"
                  data-testid="admin-password-input"
                  disabled={loading}
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full h-12 bg-[#1A2E4A] hover:bg-[#2B4468] text-white font-semibold rounded-none transition-colors duration-300"
              data-testid="admin-login-button"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Logging in...
                </span>
              ) : (
                'Login'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
